import React, { useState, useRef, useMemo, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import WorkspaceTabs from "../../../components/WorkspaceTabs";
import ReportContent from "../report/ReportContent";
import DetailsTab from "./DetailsTab";
import AddDataTab from "./AddDataTab";
import useWorkspaceData from "./useWorkspaceData";
import { sendReport, markOrderCompleted } from "../../../Services/apiService";

const TAB_KEYS = ["details", "add-data", "report"];

// Slim "Export button"-style action buttons (long + narrow), matching Orders.js's header actions.
const actionBtnSx = ({ outlined = false } = {}) => ({
  textTransform: "none",
  borderRadius: 1.5,
  px: 2.5,
  py: 0.5,
  minHeight: 0,
  fontSize: "0.8125rem",
  ...(outlined
    ? {
        borderColor: "#d1d5db",
        color: "#333",
        backgroundColor: "transparent",
        "&:hover": { backgroundColor: "#f5f5f5", borderColor: "#d1d5db" },
      }
    : {
        backgroundColor: "#163666",
        color: "#fff",
        "&:hover": { backgroundColor: "#112B51" },
      }),
});

function AnalysisWorkspace() {
  const { orderId, testId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const data = useWorkspaceData(orderId, testId);
  const {
    formData,
    setFormData,
    detailsLoading,
    detailsError,
    retryDetails,
    analysisData,
    setAnalysisData,
    additionalInfo,
    setAdditionalInfo,
    notes,
    setNotes,
    savedSnapshot,
    setSavedSnapshot,
    hasServerData,
    setHasServerData,
    savingAddData,
    setSavingAddData,
    addDataLoading,
    socketConnected,
    isTableComplete,
    isDirty,
    canSave,
    canCancel,
    canGenerateReport,
  } = data;

  const requestedTab = searchParams.get("tab");
  const activeTab = TAB_KEYS.includes(requestedTab) ? requestedTab : "details";

  // Guard against a stale/forged deep link landing on a locked Report tab.
  useEffect(() => {
    if (activeTab === "report" && !canGenerateReport) {
      const next = new URLSearchParams(searchParams);
      next.set("tab", "details");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, canGenerateReport]);

  const setActiveTab = (key) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", key);
    setSearchParams(next);
  };

  const detailsTabRef = useRef(null);
  const addDataTabRef = useRef(null);
  const reportContentRef = useRef(null);

  const [detailsHeaderState, setDetailsHeaderState] = useState({ isEditing: false, saving: false });
  const handleDetailsEditingStateChange = useCallback((isEditing, saving) => {
    setDetailsHeaderState({ isEditing, saving });
  }, []);

  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [reportActionLoading, setReportActionLoading] = useState(false);
  const [reportActionType, setReportActionType] = useState(""); // "send" | "download"

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const notify = useCallback((message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const generatedAtRef = useRef(new Date().toISOString());
  const reportData = useMemo(
    () => ({
      formData,
      analysisData,
      additionalInfo,
      notes,
      metadata: { orderId, testId, generatedAt: generatedAtRef.current },
    }),
    [formData, analysisData, additionalInfo, notes, orderId, testId]
  );

  const handleBackToAnalysis = () => navigate("/analysis");

  // The route's :orderId is the order *number*; the order detail page is keyed by the
  // internal order id, which the form-data endpoint returns in test_information.
  const orderInternalId = formData?.test_information?.order_id;
  const handleOpenOrderDetails = () => navigate(`/orders/${orderInternalId}`);

  const handleDownloadReport = async () => {
    setReportActionLoading(true);
    setReportActionType("download");
    try {
      await reportContentRef.current?.handleGenerateReport();
    } catch (err) {
      console.error("❌ Error generating PDF:", err);
      notify("Failed to generate PDF. Please try again.", "error");
    } finally {
      setReportActionLoading(false);
      setReportActionType("");
    }
  };

  const handleSendReport = async () => {
    setReportActionLoading(true);
    setReportActionType("send");
    try {
      const pdfBlob = await reportContentRef.current?.generatePdfBlob();
      const dateOfBirth = formData?.mandatory_information?.date_of_birth || null;

      try {
        await sendReport(testId, dateOfBirth, pdfBlob);

        try {
          await markOrderCompleted(orderId, testId);
        } catch (markError) {
          console.error("⚠️ Warning: Failed to mark order as completed:", markError);
        }
      } catch (sendError) {
        console.error("❌ Failed to send report, not marking as completed:", sendError);
        throw sendError;
      }

      notify(`Report sent successfully for Test ID: ${testId}`, "success");
    } catch (err) {
      console.error("❌ Error sending report:", err);
      let errorMessage = "Failed to send report. Please try again.";
      if (err.message?.includes("HTTP error")) {
        errorMessage = `Server error: ${err.message}`;
      } else if (err.message) {
        errorMessage = err.message;
      }
      notify(errorMessage, "error");
    } finally {
      setReportActionLoading(false);
      setReportActionType("");
    }
  };

  const tabs = [
    { key: "details", label: "Analysis Details" },
    { key: "add-data", label: "Add Analysis Data" },
    {
      key: "report",
      label: "Report",
      disabled: !canGenerateReport,
      disabledReason: "Complete and save all 30 analysis values first",
    },
  ];

  return (
    <Box sx={{ width: "100%", maxWidth: "100vw", overflow: "hidden" }}>
      {/* Header */}
      <Box
        sx={{
          px: 3,
          py: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <IconButton
            onClick={handleBackToAnalysis}
            sx={{
              width: 32,
              height: 32,
              padding: 0,
              backgroundColor: "#282828",
              color: "#fff",
              "&:hover": { backgroundColor: "#1a1a1a" },
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <Typography variant="h4" sx={{ fontWeight: "bold" }}>
            Analysis - {formData?.test_information?.order_number || orderId}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 1.5 }}>
          {/* Same on every tab, always the first (left-most) action. */}
          <Button
            variant="outlined"
            onClick={handleOpenOrderDetails}
            disabled={!orderInternalId}
            endIcon={<ArrowOutwardIcon sx={{ fontSize: 16 }} />}
            sx={actionBtnSx({ outlined: true })}
          >
            Order Details
          </Button>

          {activeTab === "details" && (
            <>
              <Button
                variant="contained"
                onClick={() => detailsTabRef.current?.downloadForm()}
                disabled={!formData}
                sx={actionBtnSx()}
              >
                Download Analysis Form
              </Button>
              {!detailsHeaderState.isEditing ? (
                <Button
                  variant="contained"
                  onClick={() => detailsTabRef.current?.startEdit()}
                  sx={actionBtnSx()}
                >
                  Edit Form
                </Button>
              ) : (
                <>
                  <Button
                    variant="contained"
                    onClick={() => detailsTabRef.current?.save()}
                    disabled={detailsHeaderState.saving}
                    sx={actionBtnSx()}
                  >
                    {detailsHeaderState.saving ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      "Save"
                    )}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => detailsTabRef.current?.cancel()}
                    disabled={detailsHeaderState.saving}
                    sx={actionBtnSx({ outlined: true })}
                  >
                    Cancel
                  </Button>
                </>
              )}
            </>
          )}

          {activeTab === "add-data" && (
            <Button
              variant="contained"
              onClick={() => addDataTabRef.current?.save()}
              disabled={!canSave}
              sx={actionBtnSx()}
            >
              {savingAddData ? "Saving..." : "Save Analysis"}
            </Button>
          )}

          {activeTab === "report" && (
            <>
              <Button
                variant="outlined"
                onClick={handleSendReport}
                disabled={reportActionLoading || !imagesLoaded}
                sx={actionBtnSx({ outlined: true })}
              >
                {reportActionLoading && reportActionType === "send" ? (
                  <>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    Sending...
                  </>
                ) : (
                  "Send Report"
                )}
              </Button>
              <Button
                variant="contained"
                onClick={handleDownloadReport}
                disabled={reportActionLoading || !imagesLoaded}
                sx={actionBtnSx()}
              >
                {reportActionLoading && reportActionType === "download" ? (
                  <>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    Generating...
                  </>
                ) : !imagesLoaded ? (
                  "Loading Images..."
                ) : (
                  "Download Report"
                )}
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Tab bar */}
      <Box sx={{ px: 3, pb: 2 }}>
        <WorkspaceTabs tabs={tabs} value={activeTab} onChange={setActiveTab} />
      </Box>

      {/* Tab bodies — all mounted simultaneously, toggled via display so that
          therapist-search state, the socket connection, and already-rendered
          report images survive switching tabs. */}
      <Box sx={{ display: activeTab === "details" ? "block" : "none" }}>
        <DetailsTab
          ref={detailsTabRef}
          formData={formData}
          setFormData={setFormData}
          orderId={orderId}
          testId={testId}
          loading={detailsLoading}
          error={detailsError}
          onRetry={retryDetails}
          onEditingStateChange={handleDetailsEditingStateChange}
          onNotify={notify}
        />
      </Box>

      <Box sx={{ display: activeTab === "add-data" ? "block" : "none" }}>
        <AddDataTab
          ref={addDataTabRef}
          formData={formData}
          analysisData={analysisData}
          setAnalysisData={setAnalysisData}
          additionalInfo={additionalInfo}
          setAdditionalInfo={setAdditionalInfo}
          notes={notes}
          setNotes={setNotes}
          savedSnapshot={savedSnapshot}
          setSavedSnapshot={setSavedSnapshot}
          setHasServerData={setHasServerData}
          saving={savingAddData}
          setSaving={setSavingAddData}
          loading={addDataLoading}
          socketConnected={socketConnected}
          isTableComplete={isTableComplete}
          isDirty={isDirty}
          canSave={canSave}
          canCancel={canCancel}
          canGenerateReport={canGenerateReport}
          orderId={orderId}
          testId={testId}
          onSwitchTab={() => setActiveTab("report")}
          onNotify={notify}
        />
      </Box>

      <Box sx={{ display: activeTab === "report" ? "block" : "none" }}>
        <ReportContent
          ref={reportContentRef}
          reportData={reportData}
          orderId={orderId}
          testId={testId}
          onImagesLoaded={setImagesLoaded}
        />
      </Box>

      {/* Shared Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default AnalysisWorkspace;
